/*
  ET - Header surfaces.

  Three jobs, all of them shared by the menu drawer, the dropped search panel and Impact's cart drawer:

  1. <et-menu-drawer>  — Impact's Drawer hard-forces openFrom "bottom" below 700px (assets/theme.js Drawer.openFrom),
     which silently ignores open-from="left" on every phone. Subclassing under a new tag name is the only fix that
     does not touch theme.js; re-defining x-drawer is guarded there and other drawers need the base behaviour.
  2. --et-header-bottom — the live distance from the top of the viewport to the header's bottom edge. The drawer and
     the search panel both start there, so the real header and the announcement bar above it stay lit and clickable
     and are never redrawn. The announcement bar rotates its messages at runtime (a taller sale bar with a
     countdown), so this is measured continuously, never cached at load.
  3. One open surface — the header owns a single slot. Opening any of the drawer, the search panel or the cart
     closes whichever of the other two is open, closing first so the two never animate over each other.
*/
(function () {
  'use strict';

  var READY_ATTRIBUTE = 'data-et-header-menu-ready';
  if (document.documentElement.hasAttribute(READY_ATTRIBUTE)) return;
  document.documentElement.setAttribute(READY_ATTRIBUTE, '');

  var HEADER_SELECTOR = '.shopify-section--header';
  var BAR_SELECTOR = '.shopify-section--announcement-bar';
  var OFFSET_PROPERTY = '--et-header-bottom';
  var STATE_ATTRIBUTE = 'data-et-header-open';

  /* The three ids the header can open. Impact's cart drawer is not ours, so it is addressed by id like the rest. */
  var SURFACES = ['header-sidebar-menu', 'et-header-search', 'cart-drawer'];

  /* ---------------------------------------------------------------------------------------------------------- *
   *  1. The left-opening menu drawer
   * ---------------------------------------------------------------------------------------------------------- */

  if (window.customElements) {
    window.customElements.whenDefined('x-drawer').then(function () {
      if (window.customElements.get('et-menu-drawer')) return;

      var Drawer = window.customElements.get('x-drawer');

      window.customElements.define(
        'et-menu-drawer',
        class extends Drawer {
          /* Left at every width, phones included. Impact's own getter returns "bottom" under 700px. */
          get openFrom() {
            return 'left';
          }

          /*
            From 1150px up the drawer carries .lg\:hidden, so it is display:none and there is no pointer trigger for
            it — but the theme editor still calls show() on shopify:block:select for every mega menu block, at editor
            widths well above 1150. That opened an invisible dialog which took the scroll lock with it and left
            <html class="lock"> behind. A dialog that cannot be seen must not open.
          */
          show() {
            if (window.getComputedStyle(this).display === 'none') return Promise.resolve();

            return super.show.apply(this, arguments);
          }
        }
      );
    });
  }

  /* ---------------------------------------------------------------------------------------------------------- *
   *  2. The live header offset
   * ---------------------------------------------------------------------------------------------------------- */

  function headerBottom() {
    var header = document.querySelector(HEADER_SELECTOR);
    if (!header) return 0;

    /* The bottom edge, not the height: the header is sticky and the announcement bar above it is not, so how much
       sits above the header depends on the scroll position as well as on how tall the bar currently is. */
    var bottom = Math.round(header.getBoundingClientRect().bottom);
    if (!(bottom > 0)) return 0;

    /* A sanity ceiling only. A header plus bar taller than half the viewport would leave no surface to open into. */
    return Math.min(bottom, Math.round(window.innerHeight / 2));
  }

  var lastOffset = null;

  function applyOffset() {
    var offset = headerBottom();
    if (offset === lastOffset) return;

    lastOffset = offset;
    document.documentElement.style.setProperty(OFFSET_PROPERTY, offset + 'px');
  }

  applyOffset();

  /* The announcement bar changes height on its own timer (rotating messages, a taller sale bar with a countdown),
     which fires no resize and no scroll. Border-box, because a bar that grows by padding alone leaves its content
     box untouched and a content-box observation would never fire. */
  if ('ResizeObserver' in window) {
    var observer = new ResizeObserver(function () {
      applyOffset();
    });

    var observe = function () {
      [HEADER_SELECTOR, BAR_SELECTOR].forEach(function (selector) {
        var element = document.querySelector(selector);
        if (element) observer.observe(element, { box: 'border-box' });
      });
    };

    observe();
    document.addEventListener('shopify:section:load', observe);
  }

  window.addEventListener('resize', applyOffset);
  window.addEventListener('orientationchange', applyOffset);

  /* Scroll moves the header's bottom edge (the header is sticky, the bar above it is not), but the value is only
     consumed while a surface is open, and while one is open trackOffset() below already re-reads it every frame. So
     the scroll handler is gated: an ungated getBoundingClientRect per scroll event, on every page of the site, is a
     forced layout for nothing. */
  window.addEventListener(
    'scroll',
    function () {
      if (anySurfaceOpen()) applyOffset();
    },
    { passive: true }
  );

  /*
    While a surface is open the offset is re-read every frame. A ResizeObserver only sees the elements it observes
    change SIZE — it never sees the header MOVE, which is what actually happens when the bar above it grows, is
    replaced by a taller one, or is hidden. One getBoundingClientRect per frame, only while something is open, and
    applyOffset writes nothing unless the number really changed.
  */
  var tracking = false;

  function trackOffset() {
    if (!anySurfaceOpen()) {
      tracking = false;
      return;
    }

    applyOffset();
    window.requestAnimationFrame(trackOffset);
  }

  function startTracking() {
    if (tracking) return;

    tracking = true;
    window.requestAnimationFrame(trackOffset);
  }

  /* ---------------------------------------------------------------------------------------------------------- *
   *  3. One open surface
   * ---------------------------------------------------------------------------------------------------------- */

  function surface(id) {
    var element = document.getElementById(id);
    return element && typeof element.show === 'function' ? element : null;
  }

  function anySurfaceOpen() {
    return SURFACES.some(function (id) {
      var element = document.getElementById(id);
      return element && element.hasAttribute('open');
    });
  }

  function openSurfaces(exceptId) {
    return SURFACES.filter(function (id) {
      return id !== exceptId;
    })
      .map(surface)
      .filter(function (element) {
        return element && element.hasAttribute('open');
      });
  }

  function setState(id) {
    var header = document.querySelector(HEADER_SELECTOR);
    if (header) header.setAttribute(STATE_ATTRIBUTE, id || 'none');
  }

  /* Close without handing focus back to the trigger: another surface is opening and must keep it. The dialog's own
     hide() would deactivate the focus trap with its default returnFocus, which lands after the hide transition and
     would pull focus out of whatever just opened. */
  function closeSurface(element) {
    try {
      if (element._focusTrap) {
        element._focusTrap.deactivate({ returnFocus: false, onDeactivate: function () {} });
      }
    } catch (error) {
      /* focus-trap is already inactive; hide() below still closes the dialog. */
    }

    return element.hide() || Promise.resolve();
  }

  /*
    A trigger inside a surface that is closing (the drawer's own search row) is already inert and blurred by the time
    the next surface opens, so focus-trap would record <body> as the node to return focus to and Escape would drop
    focus on the page instead of on a trigger. Move focus to the header's own on-screen trigger for the surface that
    is about to open, so the trap records that instead.
  */
  function returnFocusTarget(control, closing, id) {
    var inClosingSurface =
      !control.isConnected ||
      closing.some(function (element) {
        return element.contains(control);
      });

    if (!inClosingSurface) return;

    var replacement = Array.prototype.filter.call(
      document.querySelectorAll('[aria-controls="' + id + '"]'),
      function (candidate) {
        if (candidate.getClientRects().length === 0) return false;

        return !closing.some(function (element) {
          return element.contains(candidate);
        });
      }
    )[0];

    if (replacement && typeof replacement.focus === 'function') replacement.focus();
  }

  /* Capture phase on document, so this runs before DialogElement's own click delegate on <body> and can sequence the
     close and the open itself. Also the last chance to measure before <html class="lock"> changes the scrollbar. */
  document.addEventListener(
    'click',
    function (event) {
      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;

      var control = target.closest('[aria-controls]');
      if (!control) return;

      var id = control.getAttribute('aria-controls');
      if (SURFACES.indexOf(id) === -1) return;

      var element = surface(id);
      if (!element) return;

      applyOffset();

      var others = openSurfaces(id);
      /* Nothing else is open: leave the theme's own delegate to handle the click exactly as it always has. */
      if (others.length === 0) return;

      event.preventDefault();
      event.stopPropagation();

      if (element.hasAttribute('open')) {
        element.hide();
        return;
      }

      /*
        Close and open overlap. Waiting for the full hide (a 0.4s clip-path plus a 0.15s fade) before starting the
        0.55s open left the screen with nothing open for the best part of a second — the client sees that every time
        they tap the cart with the menu open. The spec allows a cross-fade, so the open starts once the close is
        under way: 120ms, or sooner if the hide finishes first. The scroll lock is reference counted synchronously
        on the `open` attribute, so overlapping the two cannot unbalance it.
      */
      var closing = others.map(closeSurface);
      var overlap = closing.map(function (promise) {
        return Promise.race([
          promise,
          new Promise(function (resolve) {
            window.setTimeout(resolve, 120);
          })
        ]);
      });

      Promise.all(overlap).then(function () {
        applyOffset();
        startTracking();
        returnFocusTarget(control, others, id);
        element.show();
      });
    },
    true
  );

  /* Backstop for every open that is not a click on a trigger: the cart drawer opens itself on `variant:add`, and the
     theme editor opens drawers on section select. Those cross-fade rather than close first, which the spec allows. */
  document.addEventListener('toggle', function (event) {
    var element = event.target;
    if (!element || typeof element.matches !== 'function' || !element.id) return;
    if (SURFACES.indexOf(element.id) === -1) return;

    if (!element.hasAttribute('open')) {
      if (openSurfaces(null).length === 0) setState(null);
      return;
    }

    applyOffset();
    startTracking();
    setState(element.id);
    openSurfaces(element.id).forEach(closeSurface);
  });

  /*
    After the show transition: re-read the offset (the lock may have changed the layout under it) and make sure the
    focus landed inside. Impact's focus trap only moves focus when DialogElement.initialFocus finds an input, so a
    drawer of links and accordions leaves focus on the trigger — outside the surface the spec says it must be in.
  */
  var FOCUSABLE =
    'input:not([type="hidden"]):not([disabled]), button:not([disabled]), a[href], summary, select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  document.addEventListener('dialog:after-show', function (event) {
    var element = event.target;
    if (!element || !element.id || SURFACES.indexOf(element.id) === -1) return;

    applyOffset();
    startTracking();

    if (element.contains(document.activeElement)) return;

    var first = element.querySelector(FOCUSABLE);
    if (first) first.focus();
  });
})();
